import { inject, Injectable } from '@angular/core'

import { firstValueFrom } from 'rxjs'

import { RealtimeEventType, Resource, Vehicle } from '@ti/contracts'
import { BaseCachedStore } from '@ti/core/models/cached-store'
import { RealtimeClient } from '@ti/core/realtime'

@Injectable({ providedIn: 'root' })
export class VehiclesStore extends BaseCachedStore<Vehicle> {
  protected readonly CACHE_KEY: string | (() => string) = 'TI_VEHICLES'
  protected readonly CHECK_RESOURCE_ACCESS = Resource.Vehicle
  protected readonly MAX_ITEMS = 1000

  protected readonly realtimeClient = inject(RealtimeClient)

  public async getById(id: string) {
    await this.ensureIsFresh() // Wait until data is fresh
    const vehicles = await firstValueFrom(this.store.data$)
    return vehicles?.find((vehicle) => vehicle.id === id)
  }

  public instantGroupVehicleIdsMap(): Map<string, string[]> {
    const allVehicles = this.state.items()
    const groupVehicleIdsMap = new Map<string, string[]>()

    allVehicles?.forEach((vehicle) => {
      vehicle.groupIds?.forEach((groupId) => {
        if (!groupVehicleIdsMap.has(groupId)) {
          groupVehicleIdsMap.set(groupId, [vehicle.id])
        } else {
          groupVehicleIdsMap.get(groupId)?.push(vehicle.id)
        }
      })
    })

    return groupVehicleIdsMap
  }

  protected override async init() {
    await super.init()
    this.realtimeClient
      .subscribeToEvents(RealtimeEventType.VehicleUpdated, (event) => {
        console.debug(`STORE VEHICLES New vehicle realtime refresh event:`, event)
        if (event?.data?.vehicleId) {
          this.refreshById(event.data.vehicleId)
        }
      })
      .then((unsubscribe) => this.destroy$.subscribe(unsubscribe))
  }

  protected override loadAll() {
    return firstValueFrom(this.api.fleet.vehicle.list$({ pageSize: this.MAX_ITEMS }, true))
  }

  protected override loadById(ids: string[]) {
    return firstValueFrom(
      this.api.fleet.vehicle.list$(
        {
          filter: { id: { in: ids } },
          pageSize: this.MAX_ITEMS,
        },
        true,
      ),
    )
  }

  protected override async loadSnapshot() {
    const data = await this.api.fleet.vehicle.list({
      select: ['id', 'updatedAt', 'primaryDevice.updatedAt'],
      pageSize: this.MAX_ITEMS,
    })
    return data.map((item) => this.dataToSnapshot(item))
  }

  protected override dataToSnapshot(data: Vehicle) {
    return {
      id: data.id,
      updatedAt: new Date(
        Math.max(
          new Date(data?.updatedAt ?? 0).valueOf(),
          new Date(data?.primaryDevice?.updatedAt ?? 0).valueOf(),
        ),
      ).toISOString(),
    }
  }
}
